import React, { useEffect, useRef, useState } from "react";
import { makeGet, makePost, makePatch, makeDelete } from "@culturehq/client";
import { useProfile } from "app/context";
import { Trans } from "app/utils";
import { Button, Loader, Panel, Table, Warning } from "@culturehq/components";
import ErrorMessage from "components/common/ErrorMessage";

import VirtualBackgroundModal from "./settings/virtual-backgrounds/VirtualBackgroundModal";
import VirtualBackgroundRow from "./settings/virtual-backgrounds/VirtualBackgroundRow";
import { VirtualBackground, StateProps } from "./settings/virtual-backgrounds/VirtualBackgroundTypes";

const VirtualBackgrounds = () => {
  const { profile, fetchedProfile } = useProfile();
  const isMounted = useRef<boolean>();
  const [{ loading, virtualBackgrounds, error }, setState] = useState<StateProps>({
    loading: false,
    virtualBackgrounds: [],
    error: undefined
  });

  const handleState = (
    newProps: Partial<StateProps>
  ) => setState(prev => ({ ...prev, ...newProps }));

  const handleFetch = () => {
    makeGet("/virtual_backgrounds").then(
      ({ virtualBackgrounds }: any) => isMounted.current
      && handleState({ virtualBackgrounds, loading: false }));
  };

  const handleCreate = ({ url, thumbnail, mediaType, position }: any) => {
    makePost("/virtual_backgrounds", { url, thumbnail, mediaType, position }).then(handleFetch);
  };

  const handleUpdate = ({ id, url, thumbnail, mediaType, position }: any) => {
    makePatch(`/virtual_backgrounds/${id}`, { url, thumbnail, mediaType, position }).then(handleFetch);
  };

  const handleDelete = ({ id }: VirtualBackground) => {
    makeDelete(`/virtual_backgrounds/${id}`).then(handleFetch);
  };

  useEffect(() => {
    isMounted.current = true;
    handleState({ loading: true });
    handleFetch();

    return () => {
      isMounted.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchedProfile({
      ...profile,
      organization: {
        ...profile.organization,
        virtualBackgrounds
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualBackgrounds]);

  return (
    <Panel limitWidth>
      <Panel.Heading primary>Virtual Backgrounds</Panel.Heading>
      <Panel.Body className="ManageUsers">
        {error && <ErrorMessage error={error} />}

        <VirtualBackgroundModal onCreate={handleCreate}>
          {(onTrigger: any) => (
            <Button icon="plus" primary onClick={onTrigger}>
              <Trans>Add</Trans>
            </Button>
          )}
        </VirtualBackgroundModal>

        <Loader loading={loading}>
          <Table className="no-scrollbar">
            <thead>
              <tr>
                <th><Trans>Media</Trans></th>
                <th><Trans>Type</Trans></th>
                <th><Trans>Position</Trans></th>
                <th><Trans>Created</Trans></th>
                <th><Trans>Updated</Trans></th>
                <th className="actions"><Trans>Actions</Trans></th>
              </tr>
            </thead>
            <tbody>
              {virtualBackgrounds.map(virtualBackground => (
                <VirtualBackgroundRow
                  key={virtualBackground.id}
                  virtualBackground={virtualBackground}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </Table>

          {!virtualBackgrounds.length && (
            <Warning><Trans>No results to show.</Trans></Warning>
          )}
        </Loader>
      </Panel.Body>
    </Panel>
  );
};

export default VirtualBackgrounds;
